import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import tr from "./locales/tr.json";

export const UI_LANGUAGE_STORAGE_KEY = "beas.ui.language";
export const SUPPORTED_UI_LANGUAGES = ["en", "tr"] as const;
export type UiLanguage = (typeof SUPPORTED_UI_LANGUAGES)[number];

export function isUiLanguage(value: unknown): value is UiLanguage {
  return value === "en" || value === "tr";
}

/** Map BCP-47 locale (e.g. tr-TR) or short code to a supported UI language. */
export function resolveUiLanguageFromLocale(locale?: string | null): UiLanguage {
  const language = String(locale ?? "")
    .trim()
    .split(/[-_]/)[0]
    ?.toLowerCase();
  return language === "tr" ? "tr" : "en";
}

export function readStoredUiLanguage(): UiLanguage | null {
  try {
    const stored = localStorage.getItem(UI_LANGUAGE_STORAGE_KEY);
    return isUiLanguage(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function persistUiLanguage(language: UiLanguage) {
  try {
    localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, language);
  } catch {
    // ignore quota / private mode
  }
}

export function resolveInitialUiLanguage(): UiLanguage {
  return readStoredUiLanguage() ?? resolveUiLanguageFromLocale(navigator.language) ?? "en";
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    tr: { translation: tr },
  },
  lng: resolveInitialUiLanguage(),
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
  returnNull: false,
});

export async function changeUiLanguage(language: UiLanguage) {
  persistUiLanguage(language);
  await i18n.changeLanguage(language);
  if (typeof document !== "undefined") {
    document.documentElement.lang = language;
  }
}

export default i18n;
